import { UidModel } from './../models/case.models';
import { Injectable } from '@angular/core';
import { BaseApiService, aaDataResponse } from '@shared/data-access';
import { EMPTY, Observable, catchError, map, throwError, switchMap, take } from 'rxjs';
import {
  CaseEvidenceRequest,
  NewFolderRequest,
  CaseLinkRequest,
  UpdateCaseRequest,
  MoveEvidenceFileRequest,
  RemoveCaseEvidenceRequest,
  AddCaseMemberRequest,
  PostCommentRequest,
  InviteCaseGuestsRequest,
  DownloadAllCaseEvidenceRequest,
  RemoveCaseMemberRequest,
  DownloadCaseRequest,
} from '../models/case-request.models';
import { EvidenceBase, EvidenceBaseIntrensicResponse, EvidenceLink, EvidenceLinkResponse, EvidenceOfficerBase, EvidenceStatus } from '@shared/utils/evidence-management';
import { HttpParams } from '@angular/common/http';
import {
  CaseDetailsResponse,
  CaseEvidenceResponse,
  CaseFolderResponse,
  CaseOfficerBaseResponse,
  CaseOfficerMemberResponse,
  CaseResponse,
  TagsResponse,
  CaseCommentResponse,
} from '../models/case-response.models';
import { Router } from '@angular/router';
import { Case, CaseComment, CaseOfficerBase, TableData, CaseEvidence, CaseMemberDetails } from '../models/case.models';
import { MemberType } from '../models/enums';
import { CaseDetailsFacade } from '../state/data-access.facade';
import { AppRoutes } from '@shared/utils/navigation';

@Injectable({
  providedIn: 'root',
})
export class CaseDetailsApiService {
  private caseUid$ = new Observable<string>();

  constructor(private api: BaseApiService, private caseDetailsFacade: CaseDetailsFacade, private router: Router) {
    this.caseUid$ = this.caseDetailsFacade.getCaseUid().pipe(take(1));
  }

  getCaseByUid(uid: string): Observable<CaseDetailsResponse> {
    return this.api.get<CaseDetailsResponse>(`api/cases/${uid}`).pipe(
      catchError((error) => {
        if (error.status === 403) {
          this.router.navigate(['Error', '403'], { queryParams: { backUrl: AppRoutes.Folders } });

          return EMPTY;
        }

        return throwError(() => error);
      })
    );
  }

  getCaseEvidenceFiles(): Observable<CaseEvidenceResponse[]> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.get<CaseEvidenceResponse[]>(`api/cases/${uid}/files`)));
  }

  getCaseEvidenceFolders(): Observable<CaseFolderResponse[]> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.get<CaseFolderResponse[]>(`api/cases/${uid}/folders`)));
  }

  createNewFolder(request: NewFolderRequest): Observable<string> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<string>(`api/cases/${uid}/folders`, request)));
  }

  listEvidence(skip: number = 0, searchTerm?: string): Observable<TableData<EvidenceBase>> {
    let queryParams = new HttpParams()
      .set('$orderby', 'CreatedOn desc')
      .set('$filter', `Status ne '${EvidenceStatus.QueuedForDeletion}' and Status ne '${EvidenceStatus.Deleted}'`)
      .set('$top', '10')
      .set('$skip', `${skip}`);

    if (searchTerm && searchTerm !== '') {
      queryParams = queryParams.append('search', `${searchTerm}`);
    }

    return this.api.get<aaDataResponse<EvidenceBaseIntrensicResponse>>(`api/files/odata`, queryParams).pipe(
      map(
        (response) =>
          new TableData<EvidenceBase>(
            response.iTotalRecords,
            response.aaData.map(
              (x) =>
                new EvidenceBase(
                  x.UID,
                  x.OriginalFileDate,
                  x.CreatedOn,
                  x.Title,
                  x.OrganisationalID,
                  x.OrganisationalIdCad,
                  x.FileType,
                  x.Status,
                  new EvidenceOfficerBase(x.User.UID, x.User.FirstName, x.User.LastName, x.User.EmailAddress, x.User.PictureFullPath),
                  x.ThumbnailFullPath,
                  x.IsLocked
                )
            )
          )
      )
    );
  }

  addEvidenceToCase(request: CaseEvidenceRequest): Observable<void> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<void>(`api/cases/${uid}/files`, request)));
  }

  deleteEvidenceFromCase(request: CaseEvidenceRequest): Observable<void> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.deleteWithBody<void>(`api/cases/${uid}/files`, request)));
  }

  deleteFolderFromCase(folderUid: string): Observable<void> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.delete<void>(`api/cases/${uid}/folders/${folderUid}`)));
  }

  getAllCases(skip: number = 0, searchTerm?: string): Observable<TableData<Case>> {
    const search = searchTerm ? `&search=${searchTerm}` : ``;

    return this.api.get<aaDataResponse<CaseResponse>>(`api/cases/odata?$orderby=Name&$top=10&$skip=${skip}${search}`).pipe(
      map(
        (response) =>
          new TableData(
            response.iTotalRecords,
            response.aaData.map((values) => this.createNewCase(values))
          )
      )
    );
  }

  getLinkedCases(skip: number = 0): Observable<TableData<Case>> {
    return this.caseUid$.pipe(
      switchMap((uid) =>
        this.api.get<aaDataResponse<CaseResponse>>(`api/cases/${uid}/links?$top=10&$skip=${skip}`).pipe(
          map(
            (response) =>
              new TableData(
                response.iTotalRecords,
                response.aaData.map((value) => this.createNewCase(value))
              )
          )
        )
      )
    );
  }

  addLinkCase(request: CaseLinkRequest): Observable<CaseDetailsResponse> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<CaseDetailsResponse>(`api/cases/${uid}/links`, request)));
  }

  removeLinkCase(request: CaseLinkRequest): Observable<CaseDetailsResponse> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.deleteWithBody<CaseDetailsResponse>(`api/cases/${uid}/links`, request)));
  }

  getMemberOptions(skip: number, search: string = ''): Observable<TableData<CaseOfficerBase>> {
    let params = new HttpParams();
    params = params.set('$orderby', 'FirstName');
    params = params.set('$top', 10);
    params = params.set('$skip', skip);

    if (search) {
      params = params.append('search', search);
    }

    return this.api.get<aaDataResponse<CaseOfficerBaseResponse>>('api/users/sharing', params).pipe(
      map(
        (response) =>
          new TableData(
            response.iTotalRecords,
            response.aaData.map((officerBaseResponse) => {
              return new CaseOfficerBase(
                officerBaseResponse.UID,
                officerBaseResponse.FirstName,
                officerBaseResponse.LastName,
                officerBaseResponse.EmailAddress,
                officerBaseResponse.PictureFullPath
              );
            })
          )
      )
    );
  }

  getCaseMembers(): Observable<CaseMemberDetails[]> {
    const params = new HttpParams().set('$orderby', 'FirstName');

    return this.caseUid$.pipe(
      switchMap((uid) =>
        this.api
          .get<aaDataResponse<CaseOfficerMemberResponse>>(`api/cases/${uid}/members`, params)
          .pipe(
            map((response) =>
              response.aaData.map(
                (x) =>
                  new CaseMemberDetails(
                    x.UID,
                    x.FirstName,
                    x.LastName,
                    x.EmailAddress,
                    x.PictureFullPath,
                    x.UserCaseRole,
                    x.FirstName && x.LastName ? MemberType.User : MemberType.Guest,
                    x.Customer.UID
                  )
              )
            )
          )
      )
    );
  }

  addMemberToCase(payload: AddCaseMemberRequest): Observable<number> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<number>(`api/cases/${uid}/members`, payload)));
  }

  removeMemberFromCase(payload: RemoveCaseMemberRequest): Observable<number> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.deleteWithBody<number>(`api/cases/${uid}/members`, payload)));
  }

  updateCase(updatedCaseDetails: UpdateCaseRequest): Observable<CaseDetailsResponse> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.patch<CaseDetailsResponse>(`api/cases/${uid}`, updatedCaseDetails)));
  }

  moveEvidenceFile(request: MoveEvidenceFileRequest): Observable<void> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<void>(`api/cases/${uid}/files/move`, request)));
  }

  deleteFolder(folderUid: string): Observable<void> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.delete<void>(`api/cases/${uid}/folders/${folderUid}`)));
  }

  removeCaseEvidence(request: RemoveCaseEvidenceRequest): Observable<void> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.deleteWithBody<void>(`api/cases/${uid}/files`, request)));
  }

  searchTags(searchTerm: string): Observable<TagsResponse[]> {
    return this.api.get<TagsResponse[]>(`api/tags?value=${searchTerm}`);
  }

  getCaseCommments(skip: number = 0): Observable<TableData<CaseComment>> {
    return this.caseUid$.pipe(
      switchMap((uid) =>
        this.api.get<aaDataResponse<CaseCommentResponse>>(`api/cases/${uid}/comments?$top=10&$skip=${skip}`).pipe(
          map((response) => {
            return new TableData(
              response.iTotalRecords,
              response.aaData.map(
                (value) =>
                  new CaseComment(
                    value.Case.UID,
                    value.Comment,
                    value.CreatedOn,
                    new CaseOfficerBase(value.User.UID, value.User.FirstName, value.User.LastName, value.User.EmailAddress, value.User.PictureFullPath)
                  )
              )
            );
          })
        )
      )
    );
  }

  postComment(request: PostCommentRequest): Observable<string> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<string>(`api/cases/${uid}/comments`, request)));
  }

  inviteGuestsToCase(request: InviteCaseGuestsRequest): Observable<void> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<void>(`api/cases/${uid}/guests`, request)));
  }

  uploadEvidence(payload: FormData): Observable<CaseEvidence | number> {
    return this.caseUid$.pipe(
      switchMap((uid) =>
        this.api.postFormDataEvents<CaseEvidenceResponse[]>(`api/cases/${uid}/files/upload`, payload).pipe(
          map((response: CaseEvidenceResponse[] | number): CaseEvidence | number => {
            if (typeof response === 'number') {
              return response;
            }

            const arrResponse = response;

            return new CaseEvidence(
              arrResponse[0].UID,
              new UidModel(arrResponse[0].Case.UID),
              arrResponse[0].CaseFolder ? new UidModel(arrResponse[0].CaseFolder.UID) : null,
              arrResponse[0].OriginalFileDate,
              arrResponse[0].CreatedOn,
              arrResponse[0].Title,
              arrResponse[0].OrganisationalID,
              arrResponse[0].OrganisationalIdCad,
              arrResponse[0].FileType,
              arrResponse[0].Status,
              new CaseOfficerBase(
                arrResponse[0].User.UID,
                arrResponse[0].User.FirstName,
                arrResponse[0].User.LastName,
                arrResponse[0].User.EmailAddress,
                arrResponse[0].User.PictureFullPath
              ),
              arrResponse[0].ThumbnailFullPath,
              arrResponse[0].IsLocked
            );
          })
        )
      )
    );
  }

  getEvidencePreview(evidenceUid: string): Observable<string> {
    return this.api.get<string>(`api/files/${evidenceUid}/preview?isInline=true`);
  }

  downloadAllCaseEvidence(request: DownloadAllCaseEvidenceRequest): Observable<string> {
    return this.caseUid$.pipe(switchMap((uid) => this.api.post<string>(`api/cases/${uid}/download`, request)));
  }

  getCaseDownloadAsync(request: DownloadCaseRequest): Observable<string> {
    return this.api.get<string>(`api/cases/${request.caseUid}/download/${request.requestUid}`);
  }

  getEvidenceLinks(evidenceUid: string): Observable<EvidenceLink[]> {
    return this.api.get<EvidenceLinkResponse[]>(`api/files/${evidenceUid}/links`).pipe(map((links) => links.map((x) => new EvidenceLink(x.Uid, x.Type, x.Source, x.FileName))));
  }

  private createNewCase(caseResponse: CaseResponse): Case {
    return new Case(
      caseResponse.Name,
      caseResponse.OrganisationalID,
      caseResponse.Status,
      caseResponse.UpdatedOn,
      caseResponse.CreatedOn,
      caseResponse.UID,
      new CaseOfficerBase(caseResponse.User.UID, caseResponse.User.FirstName, caseResponse.User.LastName, caseResponse.User.EmailAddress, caseResponse.User.PictureFullPath)
    );
  }
}
